import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import api from '../../services/api';
import {
    FileText, Plus, Search,
    Clock, Lock, CheckCircle, AlertCircle,
    FileWarning, Edit3, Trash2, RefreshCw, Users,
    X, History, Crown, Eye, AlertTriangle, ArrowRightLeft
} from 'lucide-react';
import CustomSelect from '../../components/ui/CustomSelect';
import ConfirmModal from '../../components/ui/ConfirmModal';
import MoveToOrgModal from '../../components/ui/MoveToOrgModal';
import Checkbox from '../../components/ui/Checkbox';
import { useTranslation } from 'react-i18next';

const STATUS_CONFIG = {
    draft:            { key: 'docs.status_draft', color: 'text-muted bg-surface-2 border-border' },
    published:        { key: 'docs.status_published', color: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' },
    pending_approval: { key: 'docs.status_pending_approval', color: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' },
    approved:         { key: 'docs.status_approved', color: 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' },
    rejected:         { key: 'docs.status_rejected', color: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' },
    locked:           { key: 'docs.status_locked', color: 'text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800' },
};

const STATUS_ICONS = {
    draft: FileText,
    published: CheckCircle,
    pending_approval: Clock,
    approved: CheckCircle,
    rejected: AlertCircle,
    locked: Lock,
};

function StatusBadge({ status }) {
    const { t } = useTranslation();
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = STATUS_ICONS[status] || FileText;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}>
            <Icon className="w-3.5 h-3.5" />
            {t(cfg.key)}
        </span>
    );
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
    });
}

export default function DocsListPage({ embedded = false, onNavigate = null }) {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const { currentOrg, currentOrgId, organizations, getLocalizedName } = useOrganization();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdminPage = location.pathname.startsWith('/admin');
    const basePath = embedded ? null : (isAdminPage ? '/admin/docs' : '/panel/docs');

    const handleNavigate = (path) => {
        if (embedded && onNavigate) {
            if (path === 'new') onNavigate('doc_new');
            else if (path.endsWith('_edit')) onNavigate(`doc_${path}`);
            else onNavigate(`doc_${path}`);
        } else if (basePath) {
            if (path === 'new') navigate(`${basePath}/new`);
            else if (path.endsWith('_edit')) navigate(`${basePath}/${path.replace('_edit', '/edit')}`);
            else navigate(`${basePath}/${path}`);
        }
    };

    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [deleting, setDeleting] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });
    const searchRef = useRef(null);

    const canCreate = user?.is_superadmin || user?.can_create_documents;
    const canDelete = user?.is_superadmin || user?.can_delete_documents;
    const canEditDoc = (doc) => user?.is_superadmin || (user?.can_edit_documents && String(doc.created_by_id) === String(user?.id));
    const [permModal, setPermModal] = useState({ open: false, docId: null, docTitle: '', viewers: [], approvers: [], allUsers: [], permError: '' });
    const [savingPerm, setSavingPerm] = useState(false);
    const [error, setError] = useState('');

    // Tashkilotga ko'chirish — Super Admin yoki can_manage_organizations, admin panelda va 2+ tashkilot bo'lsa
    const canMove = isAdminPage && organizations.length > 1 && !!(user?.is_superadmin || user?.can_manage_organizations);
    const [selected, setSelected] = useState(() => new Set());
    const [moveModal, setMoveModal] = useState({ open: false, items: [] });
    const [notice, setNotice] = useState('');

    function toggleSelect(id) {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }
    const allSelected = docs.length > 0 && docs.every(d => selected.has(d.id));
    function toggleSelectAll() {
        setSelected(allSelected ? new Set() : new Set(docs.map(d => d.id)));
    }
    function openMove(items) {
        setMoveModal({ open: true, items: items.map(d => ({ id: d.id, title: d.title })) });
    }
    function handleMoved(targetOrg, movedCount) {
        const movedIds = new Set(moveModal.items.map(i => i.id));
        setDocs(prev => prev.filter(d => !movedIds.has(d.id)));
        setSelected(new Set());
        setNotice(t('organizations.move_success', '{{count}} ta element "{{org}}" ga ko\'chirildi', {
            count: movedCount, org: getLocalizedName(targetOrg, i18n.language),
        }));
        setTimeout(() => setNotice(''), 4000);
    }

    async function fetchDocs() {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (statusFilter) params.status = statusFilter;
            if (location.pathname.startsWith('/admin')) params.admin_mode = true;
            if (currentOrgId) params.organization_id = currentOrgId;
            const { data } = await api.get('/docs', { params });
            setDocs(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { fetchDocs(); setSelected(new Set()); }, [search, statusFilter, currentOrgId]);

    async function executeDelete(doc) {
        setDeleting(doc.id);
        try {
            await api.delete(`/docs/${doc.id}`);
            setDocs(prev => prev.filter(d => d.id !== doc.id));
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Xatolik yuz berdi');
        } finally {
            setDeleting(null);
        }
    }

    async function handleDelete(doc) {
        setConfirmModal({
            open: true,
            title: t('docs.delete_doc', 'Hujjatni o\'chirish'),
            message: t('docs.delete_confirm', '\"{{title}}\" hujjatini o\'chirasizmi?', { title: doc.title }),
            onConfirm: () => {
                setConfirmModal(prev => ({ ...prev, open: false }));
                executeDelete(doc);
            }
        });
    }

    async function openPermModal(doc) {
        try {
            const [usersRes, viewersRes, approversRes] = await Promise.all([
                api.get('/docs/approver-candidates'),
                api.get(`/docs/${doc.id}/viewers`),
                api.get(`/docs/${doc.id}/approvers`),
            ]);
            setPermModal({
                open: true,
                docId: doc.id,
                docTitle: doc.title,
                createdBy: doc.created_by_id,
                allUsers: usersRes.data,
                viewers: viewersRes.data.map(v => v.user_id),
                approvers: approversRes.data.map(a => a.user_id),
            });
        } catch (e) {
            console.error(e);
        }
    }

    function handleAddUser(uid) {
        if (!uid) return;
        setPermModal(p => {
            if (p.viewers.includes(uid)) return p;
            const userObj = p.allUsers.find(u => u.id === uid);
            const newState = { ...p, viewers: [...p.viewers, uid] };
            if (userObj?.is_head_type && !p.approvers.includes(uid)) {
                newState.approvers = [...p.approvers, uid];
            }
            return newState;
        });
    }

    function removeUser(uid) {
        if (permModal.createdBy && String(uid) === String(permModal.createdBy)) {
            setPermModal(p => ({ ...p, permError: t('docs.cannot_remove_owner', "Hujjat yaratuvchisini ruxsatlardan olib tashlab bo'lmaydi!") }));
            return;
        }
        setPermModal(p => ({
            ...p,
            viewers: p.viewers.filter(id => id !== uid),
            approvers: p.approvers.filter(id => id !== uid)
        }));
    }

    function toggleApprover(uid) {
        setPermModal(p => {
            const userObj = p.allUsers.find(u => u.id === uid);
            if (!userObj?.is_head_type) return p;
            const isApprover = p.approvers.includes(uid);
            return {
                ...p,
                approvers: isApprover ? p.approvers.filter(id => id !== uid) : [...p.approvers, uid]
            };
        });
    }

    async function handleSavePerm(e) {
        e.preventDefault();
        setSavingPerm(true);
        try {
            const { data: existingViewers } = await api.get(`/docs/${permModal.docId}/viewers`);
            const existingVIds = existingViewers.map(v => v.user_id);
            for (const v of existingViewers.filter(v => !permModal.viewers.includes(v.user_id))) {
                await api.delete(`/docs/${permModal.docId}/viewers/${v.id}`);
            }
            for (const uid of permModal.viewers.filter(uid => !existingVIds.includes(uid))) {
                await api.post(`/docs/${permModal.docId}/viewers`, { user_id: uid });
            }

            const { data: existingApprovers } = await api.get(`/docs/${permModal.docId}/approvers`);
            const existingAIds = existingApprovers.map(a => a.user_id);
            for (const a of existingApprovers.filter(a => !permModal.approvers.includes(a.user_id))) {
                await api.delete(`/docs/${permModal.docId}/approvers/${a.id}`);
            }
            for (const uid of permModal.approvers.filter(uid => !existingAIds.includes(uid))) {
                await api.post(`/docs/${permModal.docId}/approvers`, { user_id: uid, order_index: 0 });
            }

            setPermModal({ open: false, docId: null, docTitle: '', viewers: [], approvers: [], allUsers: [] });
        } catch (e) {
            setPermModal(p => ({ ...p, permError: e?.response?.data?.detail || e.message || 'Xatolik yuz berdi' }));
        } finally {
            setSavingPerm(false);
        }
    }

    const statuses = ['', 'draft', 'published', 'pending_approval', 'locked', 'rejected'];

    return (
        <div className={`space-y-6 max-w-[1400px] mx-auto ${embedded ? 'p-6' : ''}`}>
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2.5">
                        <FileText className="w-7 h-7 text-brand" />
                        {t('docs.documents', 'Hujjatlar')}
                    </h1>
                    <p className="text-sm text-muted mt-1">{t('docs.management_desc', 'Loyiha hujjatlari va qoidalar boshqaruvi')}</p>
                </div>
                {canCreate && (
                    <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleNavigate('new')}
                        className="flex items-center gap-2 px-4 py-2.5 bg-brand text-inverse rounded-xl font-semibold text-sm shadow-brand-sm hover:opacity-90 transition-opacity"
                    >
                        <Plus className="w-4 h-4" />
                        {t('docs.new_document', 'Yangi hujjat')}
                    </motion.button>
                )}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                        ref={searchRef}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder={t('docs.search_placeholder', 'Hujjat qidirish...')}
                        className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
                    />
                </div>
                <div className="flex items-center gap-1.5 bg-surface border border-border rounded-xl px-1 py-1 flex-wrap">
                    {statuses.map(s => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${statusFilter === s ? 'bg-brand text-inverse' : 'text-muted hover:text-primary'}`}
                        >
                            {s ? t(`docs.status_${s}`, STATUS_CONFIG[s]?.label) : t('docs.all_statuses', 'Barchasi')}
                        </button>
                    ))}
                </div>
                <button
                    onClick={fetchDocs}
                    className="p-2.5 rounded-xl border border-border bg-surface hover:bg-surface-2 transition-colors text-muted hover:text-primary"
                    title={t('docs.refresh', 'Yangilash')}
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Success notice */}
            <AnimatePresence>
                {notice && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-sm border border-green-200 dark:border-green-800"
                    >
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        {notice}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Bulk actions bar */}
            <AnimatePresence>
                {canMove && selected.size > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 rounded-xl border border-brand/30 bg-brand/5"
                    >
                        <span className="text-sm font-semibold text-primary">
                            {t('organizations.selected_count', '{{count}} ta tanlandi', { count: selected.size })}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setSelected(new Set())}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-secondary hover:bg-surface-2"
                            >
                                {t('organizations.clear_selection', 'Tanlovni bekor qilish')}
                            </button>
                            <button
                                onClick={() => openMove(docs.filter(d => selected.has(d.id)))}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand text-inverse hover:opacity-90"
                            >
                                <ArrowRightLeft className="w-3.5 h-3.5" />
                                {t('organizations.move_title', "Tashkilotga ko'chirish")}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Table */}
            <div className="bg-surface rounded-2xl border border-border overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-20 text-muted">
                        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                        {t('docs.loading', 'Yuklanmoqda...')}
                    </div>
                ) : docs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-muted gap-3">
                        <FileWarning className="w-12 h-12 opacity-40" />
                        <p className="text-sm font-medium">{t('docs.no_documents', 'Hujjatlar topilmadi')}</p>
                        {canCreate && (
                            <button onClick={() => handleNavigate('new')} className="text-brand text-sm font-semibold hover:underline">
                                + {t('docs.create_new', 'Yangi hujjat yarating')}
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-surface-2 border-b border-border">
                                <tr>
                                    {canMove && (
                                        <th className="pl-5 pr-1 py-3 w-8 align-middle">
                                            <Checkbox
                                                id="docs-select-all"
                                                checked={allSelected}
                                                indeterminate={!allSelected && selected.size > 0}
                                                onChange={toggleSelectAll}
                                                title={t('organizations.select_all', 'Barchasini tanlash')}
                                            />
                                        </th>
                                    )}
                                    <th className="text-left px-5 py-3 text-xs font-bold text-muted uppercase tracking-wider">{t('docs.title', 'Sarlavha')}</th>
                                    <th className="text-left px-5 py-3 text-xs font-bold text-muted uppercase tracking-wider">{t('docs.status', 'Holat')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-bold text-muted uppercase tracking-wider">{t('docs.version', 'Versiya')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-bold text-muted uppercase tracking-wider">{t('docs.creator', 'Yaratuvchi')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-bold text-muted uppercase tracking-wider">{t('docs.updated_at', 'Yangilangan')}</th>
                                    <th className="text-right px-4 py-3 text-xs font-bold text-muted uppercase tracking-wider w-40"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                <AnimatePresence>
                                    {docs.map((doc, i) => (
                                        <motion.tr
                                            key={doc.id}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.03 }}
                                            className={`hover:bg-surface-2 transition-colors group ${selected.has(doc.id) ? 'bg-brand/5' : ''}`}
                                        >
                                            {canMove && (
                                                <td className="pl-5 pr-1 py-4 w-8 align-middle">
                                                    <Checkbox
                                                        id={`docs-select-${doc.id}`}
                                                        checked={selected.has(doc.id)}
                                                        onChange={() => toggleSelect(doc.id)}
                                                    />
                                                </td>
                                            )}
                                            <td className="px-5 py-4">
                                                <button
                                                    onClick={() => handleNavigate(doc.id)}
                                                    className="font-semibold text-primary hover:text-brand transition-colors line-clamp-1 flex items-center gap-2 text-left"
                                                >
                                                    {doc.is_locked && <Lock className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />}
                                                    {doc.title}
                                                </button>
                                                {doc.description && (
                                                    <p className="text-xs text-muted mt-0.5 line-clamp-1">{doc.description}</p>
                                                )}
                                            </td>
                                            <td className="px-5 py-4">
                                                <StatusBadge status={doc.status} />
                                            </td>
                                            <td className="px-4 py-4 text-sm text-muted font-mono">
                                                v{doc.current_version}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-muted">
                                                {doc.creator ? `${doc.creator.last_name_ru} ${doc.creator.first_name_ru?.charAt(0)}.` : '—'}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-muted">
                                                {formatDate(doc.updated_at || doc.created_at)}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center justify-end gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => handleNavigate(`${doc.id}/history`)}
                                                        className="p-1.5 rounded-lg hover:bg-surface-2 text-muted hover:text-primary transition-colors"
                                                        title={t('docs.history', 'Tarix')}
                                                    >
                                                        <History className="w-4 h-4" />
                                                    </button>

                                                    {canMove && (
                                                        <button
                                                            onClick={() => openMove([doc])}
                                                            className="p-1.5 rounded-lg hover:bg-brand/10 text-muted hover:text-brand transition-colors"
                                                            title={t('organizations.move_title', "Tashkilotga ko'chirish")}
                                                        >
                                                            <ArrowRightLeft className="w-4 h-4" />
                                                        </button>
                                                    )}

                                                    {isAdminPage && canEditDoc(doc) && !doc.is_locked && (
                                                        <button
                                                            onClick={() => handleNavigate(`${doc.id}_edit`)}
                                                            className="p-1.5 rounded-lg hover:bg-amber-50 text-muted hover:text-amber-600 transition-colors"
                                                            title={t('docs.edit', 'Tahrirlash')}
                                                        >
                                                            <Edit3 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {(user?.is_superadmin || doc.created_by_id === user?.id) && (
                                                        <button
                                                            onClick={() => openPermModal(doc)}
                                                            className="p-1.5 rounded-lg hover:bg-brand/10 text-muted hover:text-brand transition-colors"
                                                            title={t('docs.permissions', 'Ruxsatlar')}
                                                        >
                                                            <Users className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {canDelete && !doc.is_locked && (
                                                        <button
                                                            onClick={() => handleDelete(doc)}
                                                            disabled={deleting === doc.id}
                                                            className="p-1.5 rounded-lg hover:bg-red-50 text-muted hover:text-red-500 transition-colors disabled:opacity-50"
                                                            title={t('docs.delete', "O'chirish")}
                                                        >
                                                            {deleting === doc.id
                                                                ? <RefreshCw className="w-4 h-4 animate-spin" />
                                                                : <Trash2 className="w-4 h-4" />}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Unified Permission Modal */}
            {permModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <form onSubmit={handleSavePerm} className="bg-surface w-full max-w-lg rounded-2xl shadow-xl border border-border flex flex-col max-h-[80vh]">
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold flex items-center gap-2">
                                    <Users className="w-5 h-5 text-brand" />
                                    {t('docs.permissions', 'Ruxsatlar')}
                                </h3>
                                <p className="text-xs text-muted mt-0.5 line-clamp-1">{permModal.docTitle}</p>
                            </div>
                            <button type="button" onClick={() => setPermModal({ open: false, docId: null, docTitle: '', viewers: [], approvers: [], allUsers: [] })} className="p-1.5 rounded-lg hover:bg-surface-2 text-muted">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-5 pt-4 pb-2">
                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-primary mb-1">{t('docs.add_user', "Foydalanuvchi qo'shish")}</label>
                                <CustomSelect
                                    options={permModal.allUsers
                                        .filter(u => !permModal.viewers.includes(u.id))
                                        .map(u => ({ value: u.id, label: `${u.full_name} (${u.position || u.login})${u.is_head_type ? ' 👑' : ''}` }))
                                    }
                                    value={null}
                                    onChange={handleAddUser}
                                    placeholder={t('docs.select_user', "Foydalanuvchi tanlang...")}
                                    searchable={true}
                                    searchPlaceholder={t('docs.search_user', "Ism yoki login bo'yicha qidiring...")}
                                />
                            </div>

                            <div className="flex items-center text-xs font-bold text-muted uppercase tracking-wider mb-2">
                                <span className="flex-1">{t('docs.assigned_users', 'Biriktirilgan foydalanuvchilar')}</span>
                                <span className="w-24 text-center">{t('docs.approves', 'Tasdiqlaydi')}</span>
                                <span className="w-12 text-center"></span>
                            </div>
                        </div>

                        <div className="px-5 pb-5 overflow-y-auto space-y-2 flex-1">
                            {permModal.viewers.length === 0 ? (
                                <p className="text-muted text-sm text-center py-4">{t('docs.no_users_assigned', 'Hali hech kim biriktirilmagan')}</p>
                            ) : permModal.viewers.map(uid => {
                                const u = permModal.allUsers.find(user => user.id === uid);
                                if (!u) return null;
                                const isApprover = permModal.approvers.includes(u.id);
                                const isOwner = String(u.id) === String(permModal.createdBy);
                                return (
                                    <div key={u.id} className="flex items-center p-3 rounded-xl border border-border bg-surface hover:bg-surface-2 transition-colors">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-sm font-semibold text-primary truncate">{u.full_name}</span>
                                                {isOwner && (
                                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 flex-shrink-0" title={t('docs.owner', 'Hujjat muallifi')}>
                                                        <Crown className="w-3 h-3 text-blue-600 dark:text-blue-400" /> {t('docs.owner', 'MUALLIF')}
                                                    </span>
                                                )}
                                                {u.is_head_type && (
                                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex-shrink-0" title="Head — tasdiqlash statusi">
                                                        <Crown className="w-3 h-3" />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted truncate">{u.position || u.login}</div>
                                        </div>
                                        <div className="w-24 flex justify-center" title={!u.is_head_type ? 'Faqat Head statusidagi foydalanuvchi tasdiqlovchi bo\'la oladi' : ''}>
                                            <label className={`relative inline-flex items-center ${u.is_head_type ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={isApprover}
                                                    onChange={() => u.is_head_type && toggleApprover(u.id)}
                                                    disabled={!u.is_head_type}
                                                />
                                                <div className="w-9 h-5 bg-surface-2 border border-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-brand peer-checked:border-brand"></div>
                                            </label>
                                        </div>
                                        <div className="w-12 flex justify-end">
                                            {!isOwner ? (
                                                <button type="button" onClick={() => removeUser(u.id)} className="p-1.5 text-muted hover:text-error hover:bg-error/10 rounded-lg transition-colors" title={t('docs.remove', "O'chirish")}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <span className="text-[10px] font-bold text-muted px-2 py-1 rounded bg-surface-2 opacity-50 cursor-not-allowed" title={t('docs.owner_cannot_be_removed', "Hujjat egasini o'chirib bo'lmaydi")}>
                                                    —
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Inline error */}
                        {permModal.permError && (
                            <div className="mx-5 mb-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs border border-red-200 dark:border-red-800">
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                {permModal.permError}
                                <button type="button" onClick={() => setPermModal(p => ({...p, permError: ''}))} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                            </div>
                        )}

                        <div className="p-5 border-t border-border flex items-center justify-between bg-surface-2/30">
                            <div className="text-xs text-muted">
                                {permModal.viewers.length} {t('docs.viewers_count', "ko'ruvchi")} · {permModal.approvers.length} {t('docs.approvers_count', "tasdiqlaydi")}
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setPermModal({ open: false, docId: null, docTitle: '', viewers: [], approvers: [], allUsers: [] })} className="px-4 py-2 rounded-lg font-semibold text-secondary hover:bg-surface-2">
                                    {t('docs.cancel', 'Bekor qilish')}
                                </button>
                                <button type="submit" disabled={savingPerm} className="px-4 py-2 bg-brand text-inverse rounded-lg font-semibold disabled:opacity-50">
                                    {savingPerm ? t('docs.saving', 'Saqlanmoqda...') : t('docs.save', 'Saqlash')}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            )}

            <MoveToOrgModal
                open={moveModal.open}
                onClose={() => setMoveModal({ open: false, items: [] })}
                itemType="documents"
                items={moveModal.items}
                onMoved={handleMoved}
            />

            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={t('common.delete', 'O\'chirish')}
                cancelText={t('common.cancel', 'Bekor qilish')}
            />
        </div>
    );
}
